import { HttpClient, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, concatMap, EMPTY, finalize, from, Observable, timer } from 'rxjs';
import { environment } from 'environments/environment';
import { apiResources } from 'app/services/api/api.resources';
import { buildHttpErrorPayload, buildIngestPayload } from './pii-sanitizer';
import { ErrorIngestPayload } from './error.types';

/**
 * Fila de ingest de erros (Fase 2 — Erros MVP).
 *
 * Os payloads são ENFILEIRADOS e enviados em lote com debounce:
 *  - flush automático a cada 10s (ou imediatamente ao atingir 10 itens);
 *  - retry leve: 1 nova tentativa após 2s para 5xx/erro de rede;
 *  - 429 (throttle 60/min do backend) é descartado SILENCIOSAMENTE;
 *  - falhas do PRÓPRIO ingest nunca são reportadas (anti-loop garantido na
 *    camada do interceptor — este serviço apenas descarta o item);
 *  - no `beforeunload`, a fila é esvaziada via `navigator.sendBeacon`
 *    (o POST normal seria abortado no unload da página).
 */
@Injectable({ providedIn: 'root' })
export class ErrorReportingService
{
    private _queue: ErrorIngestPayload[] = [];
    private _flushTimer: any = null;
    private _sending = false;

    private readonly _ingestUrl =
        environment.config.apiUrl +
        apiResources.v1 +
        apiResources.errors.base +
        apiResources.errors.ingest;

    /** Tempo de debounce do flush automático (ms). */
    static readonly FLUSH_DEBOUNCE_MS = 10000;
    /** Tamanho do lote que força flush imediato. */
    static readonly MAX_BATCH = 10;
    /** Atraso do retry leve (ms). */
    static readonly RETRY_DELAY_MS = 2000;

    constructor(private _http: HttpClient)
    {
        // Esvazia a fila com sendBeacon ao fechar/recarregar a página.
        if ( typeof window !== 'undefined' )
        {
            window.addEventListener('beforeunload', () => this._flushByBeacon());
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ API pública
    // -----------------------------------------------------------------------------------------------------

    /** Enfileira um payload e agenda o flush (debounce 10s ou lote de 10). */
    report(payload: ErrorIngestPayload): void
    {
        if ( !payload )
        {
            return;
        }
        this._queue.push(payload);

        if ( this._queue.length >= ErrorReportingService.MAX_BATCH )
        {
            this.flush();
        }
        else if ( !this._flushTimer && !this._sending )
        {
            this._scheduleFlush();
        }
    }

    /** Reporta um erro HTTP 5xx com payload sanitizado (URL sem params sensíveis). */
    reportHttpError(error: HttpErrorResponse, request?: HttpRequest<any>): void
    {
        this.report(buildHttpErrorPayload(error, request));
    }

    /**
     * Reporta um erro não-tratado (runtime/zone, promise reject, etc.).
     * Erros `HttpErrorResponse` são ignorados AQUI — os 5xx já são
     * reportados pelo `HttpErrorInterceptor` (evita duplicidade).
     */
    reportUnhandledError(error: any): void
    {
        if ( error instanceof HttpErrorResponse )
        {
            return;
        }
        this.report(buildIngestPayload({
            exceptionClass: error?.name ?? (error?.constructor?.name ?? 'Error'),
            message: error?.message ?? String(error),
            stack: error?.stack,
        }));
    }

    /** Envia a fila pendente AGORA (batch serializado). */
    flush(): void
    {
        if ( this._flushTimer )
        {
            clearTimeout(this._flushTimer);
            this._flushTimer = null;
        }

        const batch = this._queue.splice(0);
        if ( batch.length === 0 )
        {
            return;
        }
        if ( this._sending )
        {
            // Já há um flush em voo — devolve os itens à fila; o flush em
            // voo reagenda quando terminar.
            this._queue.unshift(...batch);
            return;
        }

        this._sending = true;

        from(batch)
            .pipe(
                concatMap((item) => this._postWithLightRetry(item)),
                finalize(() =>
                {
                    this._sending = false;
                    // Itens que chegaram durante o envio → novo flush (debounce).
                    if ( this._queue.length > 0 )
                    {
                        this._scheduleFlush();
                    }
                })
            )
            .subscribe();
    }

    /** Nº de itens pendentes na fila (útil em testes e no unload). */
    get pendingCount(): number
    {
        return this._queue.length;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Privado
    // -----------------------------------------------------------------------------------------------------

    private _scheduleFlush(): void
    {
        if ( this._flushTimer || this._sending )
        {
            return;
        }
        this._flushTimer = setTimeout(() =>
        {
            this._flushTimer = null;
            this.flush();
        }, ErrorReportingService.FLUSH_DEBOUNCE_MS);
    }

    /**
     * POST com retry leve (1x após 2s) para 5xx/erro de rede; 429 e demais
     * códigos são descartados silenciosamente. Falhas do ingest nunca são
     * reportadas (anti-loop).
     */
    private _postWithLightRetry(item: ErrorIngestPayload): Observable<any>
    {
        return this._http.post(this._ingestUrl, item).pipe(
            catchError((error: HttpErrorResponse) =>
            {
                // 429 = throttle 60/min do backend — comportamento esperado.
                if ( error?.status === 429 )
                {
                    return EMPTY;
                }
                // 5xx / erro de rede → 1 retry após 2s; senão descarta.
                if ( (error?.status >= 500 && error?.status <= 599) || error?.status === 0 )
                {
                    return timer(ErrorReportingService.RETRY_DELAY_MS).pipe(
                        concatMap(() => this._http.post(this._ingestUrl, item).pipe(
                            catchError(() => EMPTY)
                        ))
                    );
                }
                return EMPTY;
            })
        );
    }

    private _flushByBeacon(): void
    {
        const batch = this._queue.splice(0);
        if ( batch.length === 0 )
        {
            return;
        }
        for ( const item of batch )
        {
            try
            {
                const blob = new Blob([JSON.stringify(item)], { type: 'application/json' });
                navigator.sendBeacon(this._ingestUrl, blob);
            }
            catch ( e )
            {
                // Nunca interromper o unload por falha do beacon.
            }
        }
    }
}